import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sparkles, GraduationCap, Award, Users, CheckCircle2, ArrowRight, Star, Zap, Briefcase,
  Search, Megaphone, Palette, Video, BarChart3, Wrench, Globe, Phone, MapPin, IndianRupee,
  Clock, Trophy, MessageCircle, ChevronDown,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getCoursePricing, formatINR } from "@/lib/pricing";

const fontStack = { fontFamily: "'IBM Plex Sans', system-ui, sans-serif" };
const displayStack = { fontFamily: "'Outfit', system-ui, sans-serif" };

const WHATSAPP_URL = "https://wa.me/919131341638?text=Hi%20Kamlesh%20Sir%2C%20I%20want%20to%20know%20about%20your%20training%20programs";

// ============ ICON for each badge ============
const BADGE_ICON = {
  "Most Popular": Star,
  "Niche Focus": Search,
  "Creative Career": Palette,
  "Performance Marketing": Megaphone,
  "Search Marketing": Globe,
  "Creative · Video": Video,
};
const BADGE_COLOR = {
  "Most Popular": "from-amber-500/20 to-amber-500/5 border-amber-400/40 text-amber-300",
  "Niche Focus": "from-blue-500/20 to-blue-500/5 border-blue-400/40 text-blue-300",
  "Creative Career": "from-purple-500/20 to-purple-500/5 border-purple-400/40 text-purple-300",
  "Performance Marketing": "from-rose-500/20 to-rose-500/5 border-rose-400/40 text-rose-300",
  "Search Marketing": "from-emerald-500/20 to-emerald-500/5 border-emerald-400/40 text-emerald-300",
  "Creative · Video": "from-fuchsia-500/20 to-fuchsia-500/5 border-fuchsia-400/40 text-fuchsia-300",
};

const WHY_CHOOSE = [
  { icon: GraduationCap, title: "Live Practical Training", body: "Real client projects, not just theory. Practice with live ad budgets and real websites." },
  { icon: Users, title: "Small Batch Mentorship", body: "Max 25 students per batch — direct attention, doubt sessions and group projects with peers." },
  { icon: Briefcase, title: "Job + Internship Support", body: "Placement assistance, resume reviews, mock interviews and our active hiring network." },
  { icon: Award, title: "Industry Certificates", body: "Government-recognised completion certificates + Meta/Google ads certifications support." },
  { icon: Zap, title: "AI-First Curriculum", body: "Every course teaches modern AI tools — ChatGPT, Claude, Gemini, Midjourney, Canva AI etc." },
  { icon: Trophy, title: "2000+ Students Placed", body: "Real success stories — see our student wins, salary jumps and freelance income growth." },
  { icon: CheckCircle2, title: "7-Day Money Back", body: "No questions asked refund within first week if you don't love the course." },
  { icon: Phone, title: "Lifetime WhatsApp Support", body: "Stay in our alumni group — keep learning, networking and getting help even after course ends." },
];

const SKILLS = [
  "SEO Strategy", "Google Ads", "Meta Ads", "WordPress", "Shopify", "Canva", "Photoshop", "Illustrator",
  "Premiere Pro", "After Effects", "ChatGPT", "Claude", "AI Marketing", "Email Marketing",
  "Analytics (GA4)", "Conversion Rate Optimization", "Affiliate Marketing", "Freelancing",
];

const CAREERS = [
  { title: "Digital Marketing Executive", salary: "₹3-6 LPA" },
  { title: "SEO Specialist", salary: "₹4-8 LPA" },
  { title: "Performance Marketer", salary: "₹5-12 LPA" },
  { title: "Social Media Manager", salary: "₹3-7 LPA" },
  { title: "Graphic Designer", salary: "₹3-6 LPA" },
  { title: "Video Editor", salary: "₹3-7 LPA" },
  { title: "Freelance Consultant", salary: "₹50k-₹3L/month" },
  { title: "Marketing Agency Owner", salary: "₹2L+ /month" },
];

const FAQS = [
  { q: "I'm a complete beginner — can I still join?", a: "Absolutely. All our courses start from zero. We assume no prior experience and progressively build up to advanced concepts." },
  { q: "Are classes online or offline?", a: "All courses are available in both modes. The 3-month Master Program is primarily offline at our Indore centre, with online support. SEO, Ads and Design courses are hybrid." },
  { q: "Do I get a certificate?", a: "Yes — a government-recognised completion certificate after you submit assignments and the final project. We also help you prepare for Meta and Google ads certifications." },
  { q: "What's the placement support like?", a: "Resume review, mock interviews, our active hiring network of 200+ agencies and brands, and freelance project leads. 70%+ of our students get placed within 90 days of course completion." },
  { q: "Can I pay in EMI?", a: "Yes — no-cost EMI available for all courses ₹8,000 and above via Razorpay/credit card. Contact us on WhatsApp for custom payment plans." },
  { q: "What if I'm not happy with the course?", a: "We offer 7-day no-questions-asked refund. If within the first week you feel the course isn't for you, we'll refund 100%." },
];

const TESTIMONIALS = [
  { name: "Priya Sharma", role: "SEO Freelancer", text: "Within 2 months I landed my first client at ₹15,000/month. Sir ki teaching style aur live projects ne game change kar diya." },
  { name: "Rahul Verma", role: "Marketing Executive", text: "Got placed at a digital agency with 60% salary jump. Resume review aur interview prep sessions ne bahut help ki." },
  { name: "Aakash Singh", role: "Business Owner", text: "Sir ne sikhaya kaise apne business ke Meta Ads run karne hain. Ab 30+ leads daily aate hain — agency pe depend nahi rehna padta." },
];

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/courses").then(({ data }) => { setCourses(data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  // Sort courses by badge priority (Most Popular first)
  const sorted = useMemo(() => {
    const priority = { "Most Popular": 0, "Performance Marketing": 1, "Search Marketing": 2, "Niche Focus": 3, "Creative Career": 4, "Creative · Video": 5 };
    return [...courses].sort((a, b) => (priority[a.badge] ?? 99) - (priority[b.badge] ?? 99));
  }, [courses]);

  return (
    <div className="bg-[#050505] text-white min-h-screen" style={fontStack}>
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(255,0,102,0.15)_0%,_transparent_55%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF0066]/15 border border-[#FF0066]/30 text-[#FF0066] text-xs uppercase tracking-wider font-bold">
            <Sparkles className="w-3.5 h-3.5" /> Live Practical Training
          </div>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05]" style={displayStack}>
            Programs that turn you into a<br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-[#FF0066] via-pink-400 to-amber-300 bg-clip-text text-transparent"> job-ready marketer</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
            Choose from 6 specialized programs — built around real client work, modern AI tools and 100% placement support. Indore based, India-wide reach.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-2">
            {["🎥 Live Classes", "💼 Real Projects", "🤖 AI-First Curriculum", "🎓 Industry Certificates", "👨‍💼 Placement Support", "💰 EMI Available"].map((p) => (
              <span key={p} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-white/80">{p}</span>
            ))}
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a href="#programs" className="inline-flex items-center justify-center gap-2 bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full px-7 py-3.5 text-sm transition shadow-[0_8px_30px_-8px_rgba(255,0,102,0.6)]" data-testid="hero-explore-btn">Explore programs <ArrowRight className="w-4 h-4" /></a>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold rounded-full px-7 py-3.5 text-sm transition" data-testid="hero-whatsapp-btn"><MessageCircle className="w-4 h-4" /> Talk on WhatsApp</a>
          </div>
          <div className="mt-8 inline-flex items-center gap-3 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-400/20 text-xs text-emerald-200">
            <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" /></span>
            Next batch starting soon — limited seats available
          </div>
        </div>
      </section>

      {/* ===== WHY CHOOSE ===== */}
      <section className="py-16 md:py-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.18em] text-[#FF0066] font-bold">Why CodeWithKamlesh</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold" style={displayStack}>What makes our training different</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {WHY_CHOOSE.map((w) => (
              <div key={w.title} className="group relative rounded-xl bg-[#0a0a0a] border border-white/10 hover:border-[#FF0066]/40 p-5 transition" data-testid={`why-${w.title.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="w-10 h-10 rounded-lg bg-[#FF0066]/15 border border-[#FF0066]/20 flex items-center justify-center"><w.icon className="w-4 h-4 text-[#FF0066]" /></div>
                <div className="font-bold mt-3 text-white">{w.title}</div>
                <p className="text-xs text-white/60 mt-1.5 leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROGRAMS GRID ===== */}
      <section id="programs" className="py-16 md:py-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.18em] text-[#FF0066] font-bold">Our Programs</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold" style={displayStack}>Choose your career path</h2>
            <p className="mt-3 text-sm text-white/60 max-w-xl mx-auto">6 specialized courses — each one career-ready, taught with real client work and modern AI tools.</p>
          </div>

          {loading && <div className="text-center text-white/40 py-12">Loading courses…</div>}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="courses-grid">
            {sorted.map((c) => {
              const BadgeIcon = BADGE_ICON[c.badge] || GraduationCap;
              const badgeColor = BADGE_COLOR[c.badge] || "from-white/10 to-white/5 border-white/20 text-white/80";
              const { mrp, final, hasDiscount, percentOff } = getCoursePricing(c);
              return (
                <Link
                  key={c.id}
                  to={`/courses/${c.slug || c.id}`}
                  className="group relative rounded-2xl bg-[#0a0a0a] border border-white/10 hover:border-[#FF0066]/40 overflow-hidden flex flex-col transition hover:-translate-y-1"
                  data-testid={`course-card-${c.id}`}
                >
                  {/* Top accent */}
                  <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-[#FF0066]/60 to-transparent opacity-0 group-hover:opacity-100 transition z-10" />
                  {/* Thumbnail */}
                  {c.image_url ? (
                    <div className="relative aspect-[16/9] bg-[#050505] overflow-hidden">
                      <img src={c.image_url} alt={c.title} loading="lazy" className="w-full h-full object-cover transition duration-500 group-hover:scale-105" data-testid={`course-thumb-${c.id}`} />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                      {c.badge && (
                        <div className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r ${badgeColor} border text-[10px] uppercase tracking-wider font-bold backdrop-blur-sm`}>
                          <BadgeIcon className="w-3 h-3" /> {c.badge}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="relative aspect-[16/9] bg-gradient-to-br from-[#FF0066]/30 via-[#FF0066]/10 to-[#050505] flex items-center justify-center">
                      <GraduationCap className="w-12 h-12 text-[#FF0066]/40" />
                      {c.badge && (
                        <div className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r ${badgeColor} border text-[10px] uppercase tracking-wider font-bold backdrop-blur-sm`}>
                          <BadgeIcon className="w-3 h-3" /> {c.badge}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Header */}
                  <div className="p-5 border-b border-white/5">
                    <h3 className="text-lg font-bold text-white leading-tight" style={displayStack}>{c.title}</h3>
                    {c.tagline && <p className="mt-2 text-xs text-white/65 leading-relaxed line-clamp-3">{c.tagline}</p>}
                  </div>

                  {/* Meta strip */}
                  <div className="px-5 py-3 grid grid-cols-3 gap-2 border-b border-white/5">
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-white/40">Duration</div>
                      <div className="text-xs font-semibold text-white mt-0.5 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {Math.round((c.duration_weeks || 8) / 4)} mo</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-white/40">Level</div>
                      <div className="text-xs font-semibold text-white mt-0.5 truncate">{c.level || "Beginner+"}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-white/40">Mode</div>
                      <div className="text-xs font-semibold text-white mt-0.5 truncate">{c.mode || "Online"}</div>
                    </div>
                  </div>

                  {/* Skills */}
                  {c.tags?.length > 0 && (
                    <div className="px-5 py-3 flex flex-wrap gap-1.5 border-b border-white/5">
                      {c.tags.slice(0, 6).map((t) => (
                        <span key={t} className="px-2 py-0.5 rounded-md bg-white/5 text-[10px] text-white/70 border border-white/5">{t}</span>
                      ))}
                      {c.tags.length > 6 && <span className="text-[10px] text-white/40">+{c.tags.length - 6} more</span>}
                    </div>
                  )}

                  {/* Outcome */}
                  {c.outcome && (
                    <div className="px-5 py-3 border-b border-white/5">
                      <div className="text-[9px] uppercase tracking-wider text-emerald-400 font-bold">Outcome</div>
                      <div className="text-xs text-white/80 mt-0.5">→ {c.outcome}</div>
                    </div>
                  )}

                  {/* Price + CTA */}
                  <div className="mt-auto px-5 py-4 flex items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="text-xl font-bold text-white inline-flex items-center" data-testid={`courses-list-price-${c.id}`}><IndianRupee className="w-4 h-4" />{Number(final).toLocaleString("en-IN")}</div>
                        {hasDiscount && <div className="text-xs text-white/40 line-through" data-testid={`courses-list-mrp-${c.id}`}>{formatINR(mrp, { showFree: false })}</div>}
                      </div>
                      {hasDiscount && <div className="text-[10px] text-emerald-300 font-semibold mt-0.5">Save {percentOff}%</div>}
                    </div>
                    <div className="inline-flex items-center gap-1.5 bg-[#FF0066] group-hover:bg-[#e6005c] text-white text-xs font-semibold rounded-full px-3.5 py-2 transition" data-testid={`enroll-${c.id}`}>
                      View course <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== SKILLS GRID ===== */}
      <section className="py-16 md:py-20 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.18em] text-[#FF0066] font-bold">Skills you'll master</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold" style={displayStack}>18+ in-demand tools & skills</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-2.5">
            {SKILLS.map((s) => (
              <span key={s} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm font-medium text-white/80 hover:border-[#FF0066]/40 hover:bg-[#FF0066]/5 hover:text-white transition cursor-default">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CAREER OPPORTUNITIES ===== */}
      <section className="py-16 md:py-20 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.18em] text-[#FF0066] font-bold">Career opportunities</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold" style={displayStack}>Where our students work</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {CAREERS.map((c) => (
              <div key={c.title} className="rounded-xl bg-[#0a0a0a] border border-white/10 hover:border-emerald-400/30 p-4 transition group">
                <div className="font-bold text-sm text-white group-hover:text-emerald-300 transition">{c.title}</div>
                <div className="mt-1 text-xs text-emerald-300 font-semibold">{c.salary}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-16 md:py-20 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.18em] text-[#FF0066] font-bold">Student stories</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold" style={displayStack}>Real wins from real students</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="rounded-2xl bg-[#0a0a0a] border border-white/10 p-6">
                <div className="flex gap-0.5 text-amber-400 mb-3">{[...Array(5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-current" />)}</div>
                <p className="text-sm text-white/85 leading-relaxed">"{t.text}"</p>
                <div className="mt-4 text-sm font-bold text-white">{t.name}</div>
                <div className="text-xs text-white/50">{t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TRAINER ===== */}
      <section className="py-16 md:py-20 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#FF0066] font-bold">Your trainer</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold" style={displayStack}>Meet Kamlesh Singad</h2>
            <p className="mt-4 text-sm md:text-base text-white/75 leading-relaxed">
              10+ years in digital marketing. Founder of CodeWithKamlesh — India's most practical training platform. Worked with 200+ brands on real campaigns, taught 2000+ students, and personally placed 70%+ in jobs or freelancing.
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              {["Certified Meta + Google Ads Specialist", "10+ Years Industry Experience", "2000+ Students Trained", "Active Freelance Marketer"].map((p) => (
                <li key={p} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#FF0066]" /> {p}</li>
              ))}
            </ul>
            <div className="mt-6 inline-flex items-center gap-2 text-xs text-white/60"><MapPin className="w-4 h-4 text-[#FF0066]" /> Apollo Square, Indore — visit us anytime</div>
          </div>
          <div className="aspect-square rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#FF0066]/20 to-transparent">
            <img src="https://customer-assets.emergentagent.com/job_batch-academy-2/artifacts/p8jixnzd_8a8c9b6c0fb145378e3eb14d70f06e0b.png" alt="Kamlesh Singad" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-16 md:py-20 border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.18em] text-[#FF0066] font-bold">FAQ</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold" style={displayStack}>Quick answers</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`f-${i}`} className="bg-[#0a0a0a] border border-white/10 rounded-xl px-5">
                <AccordionTrigger className="text-left text-sm md:text-base font-medium text-white hover:no-underline py-4">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-white/70 leading-relaxed pb-4">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(255,0,102,0.18)_0%,_transparent_60%)]" />
          <div className="relative">
            <h2 className="text-3xl md:text-5xl font-extrabold leading-tight" style={displayStack}>
              Ready to switch to a <span className="text-[#FF0066]">marketing career</span>?
            </h2>
            <p className="mt-4 text-white/70 max-w-xl mx-auto">Limited seats per batch. Book a call with Kamlesh Sir, get all your doubts cleared, and pick the right program for your goals.</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold rounded-full px-7 py-3.5 text-sm transition shadow-[0_8px_30px_-8px_rgba(37,211,102,0.5)]" data-testid="cta-whatsapp">
                <MessageCircle className="w-4 h-4" /> Talk on WhatsApp
              </a>
              {!user && (
                <Link to="/register" className="inline-flex items-center justify-center gap-2 bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full px-7 py-3.5 text-sm transition" data-testid="cta-register">
                  Create free account <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
            <div className="mt-6 inline-flex items-center gap-2 text-xs text-white/50">
              <Phone className="w-3.5 h-3.5" /> Call/WhatsApp: +91 91313 41638
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
